import React, { useState, useEffect } from 'react';
import { styleDefinitions as styles } from '../../theme/styles';
import { X, Palette, Check, Sun, Moon, Sparkles, Leaf, Sunset, Cloud } from 'lucide-react';
import { ThemeConfig } from '../../contexts/ThemeContext';

interface ThemeModalProps {
  onClose: () => void;
  onSave: (theme: ThemeConfig) => void;
  currentTheme?: ThemeConfig;
}

const themes: ThemeConfig[] = [
  {
    name: 'Default',
    id: 'default',
    primary: 'from-indigo-500 to-purple-600',
    secondary: 'from-indigo-50 to-purple-50',
    background: 'from-indigo-50 via-white to-purple-50',
    surface: 'bg-white/80',
    accent: 'indigo'},
  {
    name: 'Ocean Breeze',
    id: 'ocean',
    primary: 'from-blue-500 to-cyan-600',
    secondary: 'from-blue-50 to-cyan-50',
    background: 'from-blue-50 via-white to-cyan-50',
    surface: 'bg-white/80',
    accent: 'blue'},
  {
    name: 'Sunset Glow',
    id: 'sunset',
    primary: 'from-orange-500 to-pink-600',
    secondary: 'from-orange-50 to-pink-50',
    background: 'from-orange-50 via-white to-pink-50',
    surface: 'bg-white/80',
    accent: 'orange'},
  {
    name: 'Forest',
    id: 'forest',
    primary: 'from-green-500 to-emerald-600',
    secondary: 'from-green-50 to-emerald-50',
    background: 'from-green-50 via-white to-emerald-50',
    surface: 'bg-white/80',
    accent: 'green'},
  {
    name: 'Dark Mode',
    id: 'dark',
    primary: 'from-gray-700 to-gray-900',
    secondary: 'from-gray-800 to-gray-900',
    background: 'from-gray-900 via-gray-800 to-gray-900',
    surface: 'bg-gray-800/90',
    accent: 'gray'},
  {
    name: 'Lavender Dreams',
    id: 'lavender',
    primary: 'from-purple-500 to-violet-600',
    secondary: 'from-purple-50 to-violet-50',
    background: 'from-purple-50 via-white to-violet-50',
    surface: 'bg-white/80',
    accent: 'purple'},
];

const getThemeIcon = (themeId: string, styles: any) => {
  switch (themeId) {
    case 'ocean': return <Cloud style={{...styles.colors.primary}} size={20} />;
    case 'sunset': return <Sunset style={{...styles.extended.textOrange500}} size={20} />;
    case 'forest': return <Leaf style={{...styles.extended.textGreen500}} size={20} />;
    case 'dark': return <Moon style={{
     color: "var(--color-gray-600)" 
  }} size={20} />;
    case 'lavender': return <Sparkles style={{...styles.colors.secondary}} size={20} />;
    default: return <Sun style={{...styles.colors.primary}} size={20} />;
  }
};

export const ThemeModal: React.FC<ThemeModalProps> = ({ onClose, onSave, currentTheme }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<ThemeConfig>(
    currentTheme || themes[0]
  );

  // Handle ESC key press
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleSave = () => {
    localStorage.setItem('cleanRoomTheme', JSON.stringify(selectedTheme));
    onSave(selectedTheme);
    onClose();
  };

  return (
    <div style={{...styles.position.fixed, zIndex: 1000 }} onClick={onClose}>
      <div style={{...styles.spacing.py4, 
    ...styles.size.minHScreen,
    ...styles.spacing.px4,
    ...styles.layout.flex,
     alignItems: "center" ,
     justifyContent: "center" 
  }}>
        <div style={{
    ...styles.background.white,
     borderRadius: "var(--radius-2xl)" ,
     maxWidth: "42rem" ,
    ...styles.size.wFull,
    ...styles.spacing.paddingXl
  }} onClick={(e) => e.stopPropagation()}>
        <div style={{
    ...styles.layout.flex,
     justifyContent: "space-between" ,
     alignItems: "center" ,
    ...styles.spacing.marginBottomXl
  }}>
          <div style={{
    ...styles.layout.flex,
     alignItems: "center" ,
    ...styles.spacing.gap3
  }}>
            <div style={{...styles.extended.gradientBluePurple, 
    ...styles.spacing.paddingSm,
    ...styles.utility.roundedLg
  }}>
              <Palette style={{...styles.typography.textWhite}} size={20} />
            </div>
            <h2 style={{
    ...styles.typography.textXl,
    ...styles.typography.fontBold,
     color: "var(--color-gray-900)" 
  }}>Choose Your Theme</h2>
          </div>
          <button
            onClick={onClose}
            style={{
    ...styles.spacing.paddingSm ,
    ...styles.utility.roundedLg,
  }}
          >
            <X size={20} style={{
     color: "var(--color-gray-600)" 
  }} />
          </button>
        </div>

        <div style={{...styles.extended.gridCols2, 
    ...styles.layout.grid,
    ...styles.spacing.gap4,
    ...styles.spacing.marginBottomXl
  }}>
          {themes.map((theme) => (
            <button
              key={theme.id}
              onClick={() => setSelectedTheme(theme)}
              
            >
              {selectedTheme.id === theme.id && (
                <div style={{...styles.position.absolute}}>
                  <div style={{...styles.colors.primary, 
    ...styles.spacing.paddingXs,
    ...styles.utility.roundedFull
  }}>
                    <Check size={12} style={{...styles.typography.textWhite}} />
                  </div>
                </div>
              )}
              
              <div style={{
    ...styles.layout.flex,
    ...styles.layout.flexColumn,
     alignItems: "center" ,
    ...styles.spacing.gap3
  }}>
                {getThemeIcon(theme.id, styles)}
                <div style={{...styles.typography.textCenter}}>
                  <p style={{
    ...styles.typography.fontMedium,
     color: "var(--color-gray-900)" ,
    ...styles.typography.textSm
  }}>{theme.name}</p>
                </div>
                <div >
                  <div style={{...styles.position.absolute, ...{ inset: "0.5rem" }, ...styles.utility.rounded}}></div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div style={{...styles.extended.pt4, 
    ...styles.layout.flex,
     justifyContent: "flex-end" ,
    ...styles.spacing.gap3,
    ...styles.utility.borderTop
  }}>
          <button
            onClick={onClose}
            style={{
     color: "var(--color-gray-700)" ,
    ...styles.background.gray100,
    ...styles.utility.roundedLg ,
    ...styles.typography.fontMedium
  }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} style={{...(isHovered ? styles.states.hover.gray50 : {...styles.extended.px5, ...styles.extended.py25}),
    ...styles.typography.textWhite,
    ...styles.utility.roundedLg,
    ...styles.typography.fontMedium
  }}
          >
            Apply Theme
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};